import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, Lock, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/auth";
import { useSpace } from "@/lib/space";
import { VoiceButton } from "@/components/quantum/TeachingCards";
import Seal from "@/components/quantum/Seal";

const area = "mt-3 w-full min-h-[120px] resize-y bg-[#0F070B] border border-[#D9B47A]/20 p-4 text-[16px] leading-relaxed text-[#F2E8DF] placeholder:text-[#8F8176] focus:outline-none focus:border-[#E8838E] transition-colors";

const ArielNote = ({ text, testId }) => (
  <motion.div data-testid={testId} initial={{ opacity: 0.3, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-6 border-l-2 border-[#E8838E] bg-[#140910] p-5">
    <div className="flex items-center gap-3">
      <img src="/images/goddess-ariel-avatar.jpg" alt="" className="w-9 h-9 rounded-full object-cover" />
      <p className="font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#E8838E]">Ariel responds</p>
      <div className="ml-auto"><VoiceButton text={text} testId={`${testId}-voice`} /></div>
    </div>
    <p className="mt-4 font-display italic text-[19px] leading-relaxed text-[#F2E8DF]">{text}</p>
  </motion.div>
);

// Journaling prompts, saved privately; Ariel can respond to what was written.
export const Reflect = ({ g, prompts, saved, savedReply, onDone }) => {
  const [answers, setAnswers] = useState(() => prompts.map((_, k) => saved?.[k] ?? ""));
  const [busy, setBusy] = useState("");
  const [reply, setReply] = useState(savedReply ?? "");
  const { api: prefix } = useSpace();
  const persist = () => api.put(`${prefix}/gates/${g}/reflections`, { answers });
  const save = async (next) => {
    setBusy("save");
    try {
      await persist();
      toast.success("Your reflections are kept privately.");
      if (next) onDone();
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };
  const ask = async () => {
    setBusy("ariel");
    try {
      await persist();
      const { data } = await api.post(`${prefix}/gates/${g}/reflect-reply`);
      setReply(data?.reply ?? "");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };
  const wrote = answers.some((a) => a.trim());
  return (
    <div data-testid="qi-reflect">
      <p className="mt-6 text-[15px] text-[#A8998C] flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-[#D9B47A]" /> Only you, and Ariel when you ask, can see what you write.</p>
      {prompts.map((q, k) => (
        <label key={k} className="block mt-8">
          <span className="font-display text-xl leading-snug">{q}</span>
          <textarea data-testid={`qi-reflect-${k}`} maxLength={4000} value={answers[k]} onChange={(e) => setAnswers((a) => a.map((v, j) => (j === k ? e.target.value : v)))} placeholder="Write freely…" className={area} />
        </label>
      ))}
      <button data-testid="qi-reflect-ariel" onClick={ask} disabled={!!busy || !wrote} className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#E8838E]/50 px-5 py-3 font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#E8838E] hover:bg-[#E8838E] hover:text-[#0A0706] transition-colors disabled:opacity-40">
        {busy === "ariel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {busy === "ariel" ? "Ariel is reading…" : "Ask Ariel to respond"}
      </button>
      {reply && <ArielNote text={reply} testId="qi-reflect-reply" />}
      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <button data-testid="qi-reflect-save" onClick={() => save(false)} disabled={!!busy} className="rounded-full border border-[#D9B47A]/40 px-6 py-3.5 font-tt-mono text-[12px] uppercase tracking-[0.2em] text-[#D9B47A] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors disabled:opacity-60">{busy === "save" ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Save"}</button>
        <button data-testid="qi-reflect-next" onClick={() => save(true)} disabled={!!busy} className="flex-1 rounded-full bg-[#D9B47A] text-[#0A0706] py-3.5 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors disabled:opacity-60">Save and check in</button>
      </div>
    </div>
  );
};

// "What did you feel?" — completing it earns the gate's seal, opens the next gate, and Ariel replies.
export const Checkin = ({ g, checkin, saved, onComplete, next }) => {
  const space = useSpace();
  const [choice, setChoice] = useState(saved?.choice ?? null);
  const [note, setNote] = useState(saved?.note ?? "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(Boolean(saved?.at));
  const [reply, setReply] = useState(saved?.reply ?? "");
  const [fresh, setFresh] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${space.api}/gates/${g}/checkin`, { choice, note });
      setReply(data?.reply ?? "");
      setFresh(!done);
      setDone(true);
      onComplete();
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="qi-checkin">
      <h3 className="mt-8 font-display text-3xl leading-tight">{checkin.question}</h3>
      <div className="mt-6 flex flex-wrap gap-2">
        {checkin.options.map((o, k) => (
          <button key={o} data-testid={`qi-checkin-option-${k}`} onClick={() => setChoice(choice === o ? null : o)} aria-pressed={choice === o} className={`rounded-full border px-4 py-2.5 text-[15px] transition-colors ${choice === o ? "bg-[#E8838E] border-[#E8838E] text-[#0A0706]" : "border-[#E8838E]/40 text-[#DCCFC3] hover:border-[#E8838E]"}`}>{o}</button>
        ))}
      </div>
      <textarea data-testid="qi-checkin-note" maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Or describe it in your own words…" className={area} />
      <button data-testid="qi-checkin-submit" onClick={submit} disabled={busy || (!choice && !note.trim())} className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C2414F] text-[#F2E8DF] py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {busy ? "Ariel is listening…" : done ? "Update my check-in" : "Complete this gate"}
      </button>
      {done && (
        <div data-testid="qi-checkin-done" className="mt-8 border border-[#D9B47A]/30 bg-[#140910] p-6 text-center">
          <motion.div initial={fresh ? { scale: 0.4, rotate: -40, opacity: 0.2 } : false} animate={{ scale: 1, rotate: 0, opacity: 1 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }} className="inline-block">
            <Seal g={g} earned size={120} testId="qi-seal-earned" />
          </motion.div>
          <p className="mt-4 font-display italic text-2xl text-[#D9B47A]">The seal of this gate is yours. Blessed Be.</p>
          {next != null ? (
            <Link to={space.gate(next)} data-testid="qi-next-gate" className="mt-5 inline-flex items-center gap-2 font-tt-mono text-[12px] uppercase tracking-[0.2em] text-[#F2E8DF] hover:text-[#D9B47A]">Enter the next gate <ArrowRight className="w-4 h-4" /></Link>
          ) : (
            <Link to={space.base} data-testid="qi-back-map" className="mt-5 inline-flex items-center gap-2 font-tt-mono text-[12px] uppercase tracking-[0.2em] text-[#F2E8DF] hover:text-[#D9B47A]">Back to the gate map <ArrowRight className="w-4 h-4" /></Link>
          )}
        </div>
      )}
      {done && reply && <ArielNote text={reply} testId="qi-checkin-reply" />}
    </div>
  );
};
